import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, Award, GraduationCap, Heart, Users, Hash, ImageOff } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SocialButtons } from "@/components/SocialButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

export default function AboutPage() {
  const [aboutPhoto, setAboutPhoto] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site-settings`).then((r) => {
      setAboutPhoto(r.data.about_photo || "");
    }).catch(() => {
      setAboutPhoto("");
    });
  }, []);
  const credentials = [
    { icon: Clock, title: "2,300+ Hours", desc: "Coaching & Training" },
    { icon: Award, title: "Certified", desc: "BodyTalk Practitioner" },
    { icon: GraduationCap, title: "30 Years", desc: "Supporting Individuals, Parents & Children" },
    { icon: Heart, title: "1,000+", desc: "Clients Helped" },
    { icon: Users, title: "Mother of 3", desc: "Lived parenting experience" },
    { icon: Hash, title: "Certified", desc: "Talent Numerology Consultant" },
  ];

  if (!aboutPhoto) {
    return (
      <div className="min-h-screen bg-champagne" data-testid="about-page">
        <Navbar />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-champagne" data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6" data-testid="about-hero">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} className="lg:col-span-5">
              <div className="rounded-[2rem] overflow-hidden shadow-lift">
                {aboutPhoto ? (
                  <img src={aboutPhoto} alt="Joyce" className="w-full aspect-[3/4] object-cover" />
                ) : (
                  <div className="w-full aspect-[3/4] bg-champagne-dark flex items-center justify-center">
                    <ImageOff className="w-12 h-12 text-soft-grey" />
                  </div>
                )}
              </div>
              <SocialButtons variant="icon-only" className="mt-4 justify-center" />
            </motion.div>
            <div className="lg:col-span-7 space-y-6">
              <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={0}
                className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-charcoal leading-tight"
              >
                A steady guide through<br />
                <span className="italic text-navy">emotional complexity</span>
              </motion.h1>
              <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={1}
                className="font-body text-charcoal-light leading-relaxed text-lg"
              >
                Joyce is an intuitive coach, certified BodyTalk practitioner, and certified Talent Numerology
                consultant with over three decades of experience supporting emotional growth in individuals,
                parents, and children.
              </motion.p>
              <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={2}
                className="font-body text-charcoal-light leading-relaxed text-lg"
              >
                Her work focuses on helping people understand the deeper patterns behind their emotions,
                behaviors, and relationships — and guiding them toward meaningful, lasting change.
              </motion.p>
              <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={3}
                className="font-body text-charcoal-light leading-relaxed"
              >
                As a mother of three, Joyce understands the realities of parenting, emotional responsibility,
                and self-regulation not as theory, but as lived experience.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Grid */}
      <section className="py-20 bg-white" data-testid="about-credentials">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {credentials.map((c, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i * 0.5}
                className="bg-off-white border border-border rounded-2xl p-6 text-center hover:shadow-soft transition-all"
              >
                <div className="w-12 h-12 bg-navy/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <c.icon className="w-6 h-6 text-navy" />
                </div>
                <p className="font-heading text-2xl font-bold text-charcoal">{c.title}</p>
                <p className="font-body text-sm text-charcoal-light mt-1">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center" data-testid="about-cta">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal mb-6">
            Understanding is the first step to change.
          </h2>
          <a
            href="https://wa.me/6597946649"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-cta-btn"
            className="inline-flex items-center gap-2 bg-navy text-white px-8 py-4 rounded-full font-body font-semibold text-lg hover:bg-navy-light transition-all hover:scale-105 shadow-soft"
          >
            Book a Session
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
